/**
 * @license
 * Copyright© Celligence LLC - 2023
 * This software program is protected by copyright laws and is the property of
 * Celligence LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 */
import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import axios from "axios";
// import Banner from "./components/BannerNew/BannerNew.js";
import Banner from "./components/BannerAngelAi/BannerAngelAi.js";
import Body from "./components/Body/Body.js"
import useData from "./components/Utils/useData.js";
import { Helmet } from "react-helmet";
import MediaQuery from "react-responsive";
import Script from "./components/widgets/widget.js";
import { CookieSetting } from "cookiesetting-component";
import { useUserLocation } from "./components/Utils/LocationContext";
import useNYStateDetection from "./components/Utils/useNYStateDetection";
import OhNoErrorPageNY from "./components/OhNoErrorPageNY";
const FairLending = lazy(() => import("./components/FairLending/FairLending.js"));
const AngelAppSection = lazy(() => import('./components/AngelAppSection/AngelApp.js'));
const PopularRequests = lazy(() => import('./components/PopularRequests/index.js'));
const FinancialSupportDesktop = lazy(() => import('./components/FinancialSupport/FinancialSupportDesktop.js'));
const FinancialSupportMobile = lazy(() => import('./components/FinancialSupport/FinancialSupportMobile.js'));
const FinancingNeeds = lazy(() => import('./components/FinancingNeeds/FinancingNeeds.js'));
const AngelaiVsMobileDesktop = lazy(() => import('./components/AngelaiVsMobileDesktop/AngelaiVsMobileDesktop.js'));
const EasyNavigationVideos = lazy(() => import('./components/EasyNavigationVideos/EasyNavigationVideos/EasyNavigationVideos.js'));
const HowItWorks = lazy(() => import('./components/HowItWorks/HowItWorks.js'));
const WhyChooseSunwest = lazy(() => import('./components/WhyChooseSunwest/WhyChooseSunwest.js'));
const Consumer = lazy(() => import('./components/Consumer/Consumer.js'));
const AngelaiExpanding = lazy(() => import('./components/AngelaiExpanding/AngelaiExpanding.js'));
const Testimonials = lazy(() => import('./components/Testimonials/Testimonials.js'));
const FastCompany = lazy(() => import('./components/FastCompany/FastCompany.js'));
const AngelTwin = lazy(() => import('./components/AngelTwin/AngelTwin.js'));
const ResourcesMenu = lazy(() => import('./components/ResourcesMenu/index.js'));
const BannerVideoSection = lazy(() => import('./components/BannerVideoSection/BannerVideoSection.js'));
const BorrowerTestimonial = lazy(() => import("./components/VideoCarousel/VideoCarousel.js"));

// const Script = lazy(() => import('./components/widgets/widget.js'));
function App() {
  const userLocation = useUserLocation();
  const isNYState = useNYStateDetection(userLocation);
  const [showOtherComponents, setShowOtherComponents] = useState(false);
  const [differencesData, setDifferencesData] = useState([]);
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const scrollPosition =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      const maxScroll = documentHeight - windowHeight;
      const percentage = (scrollPosition / maxScroll) * 100;

      if (percentage > 50) {
        setShowOtherComponents(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  
  useEffect(() => {
    const fetchAngelAiJSON = async () => {
      try {
        const timestamp = Date.now(); // Generate a unique timestamp
        const url = `${process.env.REACT_APP_MAIN_PAGE}?timestamp=${timestamp}`;
        const response = await axios.get(url);
        setDifferencesData(response.data.differences);
      } catch (error) {
        console.error('Error fetching differences data:', error);
      }
    };
    fetchAngelAiJSON();
  }, []);
  
  const data = useData();
  if (data === null) {
    return (<div />)
  }

  // Show NY error page if user is in New York state
  if (isNYState) {
    return <OhNoErrorPageNY />;
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.angelai.life/" />
        <title>AI-Powered Mortgage Solutions | AngelAi.life – Stress-Free Home Loans</title>
        <meta name="description" content="Experience stress-free home loans with AngelAi.life, your AI-powered mortgage lender. Enjoy lightning-fast closings, fair lending, 24/7 support, and trusted guidance." />
        <meta
          name="keywords"
          content="AI mortgage, AI lender, AngelAi Life, AngelAi Lender, AngelAi Mortgage, AI mortgage lender, AngelAi.life, home loans, refinance, purchase, mortgage, homebuying, AI in mortgage, AI in home finance, AI in mortgage financing, fair lending, fast closings, AI loan approvals, stress-free mortgage, trusted mortgage solutions, borrower testimonials, expert advisors, 24/7 support, no hidden fees"
        />
      </Helmet>
      <MediaQuery maxWidth={767}>
        <Banner fetchpriority="high" data={data} />
        <Suspense fallback={<div />}>
          <BannerVideoSection />
        </Suspense>


        <Script />

        {showOtherComponents && (
          <>
            <Suspense fallback={<div />}>
              <AngelAppSection data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <PopularRequests data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FinancialSupportMobile data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelaiVsMobileDesktop data={data} differences={differencesData} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FinancingNeeds data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <EasyNavigationVideos data={data} />
            </Suspense>

            

            <Suspense fallback={<div />}>
              <HowItWorks data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <Consumer data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelaiExpanding data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <Testimonials data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FastCompany data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelTwin data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <WhyChooseSunwest data={data} />
            </Suspense>

          </>
        )}
      </MediaQuery>

      <MediaQuery minWidth={768}>
        <Banner fetchpriority="high" data={data} />

        <Script />

        <Suspense fallback={<div />}>
          <BannerVideoSection />
        </Suspense>

        <Suspense fallback={<div />}>
          <AngelAppSection data={data} />
        </Suspense>


        {showOtherComponents && (
          <>
            <Suspense fallback={<div />}>
              <PopularRequests data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FinancialSupportDesktop data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelaiVsMobileDesktop data={data} differences={differencesData} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FinancingNeeds data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <EasyNavigationVideos data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <HowItWorks data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <Consumer data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelaiExpanding data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <Testimonials data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <FastCompany data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <AngelTwin data={data} />
            </Suspense>

            <Suspense fallback={<div />}>
              <WhyChooseSunwest data={data} />
            </Suspense>

          </>
        )}
      </MediaQuery>
      <Suspense>
        <CookieSetting
          privacyPolicyLink={process.env.REACT_APP_PRIVACY_POLICY_LINK}
          configUrl={process.env.REACT_APP_COOKIE_SETTING_JSON}
          analytics={process.env.REACT_APP_ANALYTICS_PATH}
        />
      </Suspense>
    </>
  );
}

export default App;
